/**
 * Road ops (TD §5): add, delete, dot editing, cut, join, attach, detach and widths.
 * Junctions are structural: a branch's end dot is also a dot of the host road, and every op keeps it that way.
 */
import type { NodeId, PlanDoc, Pt, RoadId, RoadKind } from '../../shared/types';
import { clamp, cumLengths, dist, lerpAt, nearestOnPoly, pointAlong, polyLen, SEG } from '../geom/basic';
import { roadPoly } from '../geom/shapes';
import { minRoadNodes, roadsUsingNode, whyCantRemoveNode } from '../model/query';
import { ok, refuse, type OpResult } from './result';
import { addNode, dedupeConsecutive, freshId, NODE_W_MAX, NODE_W_MIN, pruneOrphans } from './util';

/** Width of a newly drawn road in metres (the prototype's default path). */
export const DEFAULT_ROAD_W = 5;
/** An existing dot this close (m) is reused when attaching, instead of inserting a new one (TD §5 addRoad). */
export const ATTACH_REUSE_M = 1.5;
/** Extra gap (m) a cut leaves on top of the local road width (TD §5 cutRoad). */
export const CUT_EXTRA_GAP_M = 3;

const loopOf = (closed: boolean, n: number) => closed && n >= 3;

/**
 * Inserts a new dot into a road, in the span nearest to (x, y), with the road's width at that spot.
 * With `onLine` the dot goes on the centre line (the nearest spot); otherwise exactly at (x, y).
 */
export function insertNode(draft: PlanDoc, args: { roadId: RoadId; x: number; y: number; onLine?: boolean }): OpResult<NodeId> {
  const r = draft.roads[args.roadId];
  if (!r) return refuse('That road no longer exists.');
  const poly = roadPoly(draft, r), n = r.nodes.length;
  if (poly.pts.length < 2) return refuse('That road has no shape.');
  const q = nearestOnPoly(poly.pts, args.x, args.y);
  const span = clamp(Math.floor(q.i / SEG), 0, (loopOf(r.closed, n) ? n : n - 1) - 1);
  const [x, y] = args.onLine ? [q.px, q.py] : [args.x, args.y];
  const id = addNode(draft, x, y, lerpAt(poly.widths, q.i, q.t));
  r.nodes.splice(span + 1, 0, id);
  return ok(id);
}

/**
 * Makes sure the road has a dot at (x, y) and returns it: an existing dot within 1.5 m, otherwise a new dot
 * inserted on the centre line at the nearest spot. This is how a road end attaches to a host road.
 */
export function nodeOnRoad(draft: PlanDoc, args: { roadId: RoadId; x: number; y: number }): OpResult<NodeId> {
  const r = draft.roads[args.roadId];
  if (!r) return refuse('That road no longer exists.');
  let best: NodeId | null = null, bd = ATTACH_REUSE_M;
  for (const id of r.nodes) {
    const nd = draft.nodes[id];
    if (!nd) continue;
    const d = Math.hypot(nd.x - args.x, nd.y - args.y);
    if (d < bd) { bd = d; best = id; }
  }
  if (best) return ok(best);
  return insertNode(draft, { ...args, onLine: true });
}

/** Where a drawn road point snapped to: a host road (attach there), optionally an exact existing dot. */
export interface RoadSnap { roadId: RoadId; nodeId?: NodeId }

/**
 * Adds a road through `points`. A point with a snap attaches to that road (TD §5): it reuses the snapped dot,
 * or a dot within 1.5 m, or inserts one on the host road, so the junction is one shared dot.
 * Refuses when fewer than 2 distinct dots (3 for a loop) would remain.
 */
export function addRoad(draft: PlanDoc, args: {
  points: readonly Pt[];
  snaps?: readonly (RoadSnap | null | undefined)[];
  w?: number;
  kind?: RoadKind;
  closed?: boolean;
}): OpResult<RoadId> {
  const closed = !!args.closed, w = args.w ?? DEFAULT_ROAD_W;
  // Check before touching the draft: where would each point end up?
  const where = args.points.map((p, i): Pt => {
    const s = args.snaps?.[i], nd = s?.nodeId ? draft.nodes[s.nodeId] : undefined;
    return nd ? [nd.x, nd.y] : [p[0], p[1]];
  });
  const distinct = where.filter((p, i) => i === 0 || dist(p, where[i - 1] as Pt) > 0.01);
  if (distinct.length < minRoadNodes(closed)) return refuse(closed ? 'A loop needs at least 3 points.' : 'A road needs at least 2 points.');
  for (const s of args.snaps ?? []) if (s && !draft.roads[s.roadId]) return refuse('The road to attach to no longer exists.');

  const ids: NodeId[] = args.points.map((p, i) => {
    const s = args.snaps?.[i];
    if (s?.nodeId && draft.nodes[s.nodeId]) return s.nodeId;
    if (s) { const res = nodeOnRoad(draft, { roadId: s.roadId, x: p[0], y: p[1] }); if (res.ok) return res.value; }
    return addNode(draft, p[0], p[1], w);
  });
  const nodes = dedupeConsecutive(ids, closed);
  const id = freshId(draft.roads);
  draft.roads[id] = { nodes, closed: closed && new Set(nodes).size >= 3, kind: args.kind ?? 'path' };
  return ok(id);
}

/** Deletes a road. Junction dots stay for the other roads; dots nobody uses any more are removed. */
export function deleteRoad(draft: PlanDoc, args: { roadId: RoadId }): OpResult {
  if (!draft.roads[args.roadId]) return refuse('That road no longer exists.');
  delete draft.roads[args.roadId];
  pruneOrphans(draft);
  return ok(true);
}

/**
 * Removes a dot from every road that uses it (like removeVert for corners). Refuses if any road would keep
 * fewer than 2 distinct dots, or a loop fewer than 3 (R12).
 */
export function removeNode(draft: PlanDoc, args: { nodeId: NodeId }): OpResult {
  const why = whyCantRemoveNode(draft, args.nodeId);
  if (why) return refuse(why);
  for (const rid of roadsUsingNode(draft, args.nodeId)) {
    const r = draft.roads[rid];
    if (r) r.nodes = dedupeConsecutive(r.nodes.filter(n => n !== args.nodeId), r.closed);
  }
  delete draft.nodes[args.nodeId];
  return ok(true);
}

/** Detaches a road from a junction: the road gets its own copy of the shared dot (TD §4 "Detach"). */
export function detachNode(draft: PlanDoc, args: { roadId: RoadId; nodeId: NodeId }): OpResult<NodeId> {
  const r = draft.roads[args.roadId], nd = draft.nodes[args.nodeId];
  if (!r || !nd || !r.nodes.includes(args.nodeId)) return refuse('That dot is not on this road.');
  if (roadsUsingNode(draft, args.nodeId).length < 2) return refuse('That dot is not a junction.');
  const copy = addNode(draft, nd.x, nd.y, nd.w);
  r.nodes = r.nodes.map(n => (n === args.nodeId ? copy : n));
  return ok(copy);
}

/** Sets the width at one dot (clamped to 0.5–100 m). At a junction this is the width for every road there. */
export function setNodeWidth(draft: PlanDoc, args: { nodeId: NodeId; w: number }): void {
  const nd = draft.nodes[args.nodeId];
  if (nd) nd.w = clamp(args.w, NODE_W_MIN, NODE_W_MAX);
}

/** Sets the width at every dot of a road (clamped to 0.5–100 m). */
export function setRoadWidth(draft: PlanDoc, args: { roadId: RoadId; w: number }): void {
  const r = draft.roads[args.roadId];
  if (!r) return;
  for (const nid of new Set(r.nodes)) setNodeWidth(draft, { nodeId: nid, w: args.w });
}

/** Changes what kind of road this is (main, cross, path, entrance). */
export function setRoadKind(draft: PlanDoc, args: { roadId: RoadId; kind: RoadKind }): void {
  const r = draft.roads[args.roadId];
  if (r) r.kind = args.kind;
}

/**
 * Cuts a road at the spot nearest (x, y), leaving a gap of the local width plus 3 m (TD §5), so the rounded
 * ends stay 3 m apart. An open road becomes up to two roads; a loop opens into one road. Each new end gets a
 * new dot with the local width; all other dots keep their ids, so junctions on the kept parts stay shared.
 * The first piece keeps the road's id. Pieces of 3 m or less are dropped. Returns the ids of the pieces left.
 */
export function cutRoad(draft: PlanDoc, args: { roadId: RoadId; x: number; y: number }): OpResult<RoadId[]> {
  const r = draft.roads[args.roadId];
  if (!r) return refuse('That road no longer exists.');
  const { pts: P, widths: W } = roadPoly(draft, r);
  if (P.length < 2) return refuse('That road has no shape.');
  const n = r.nodes.length, closed = loopOf(r.closed, n);
  const q = nearestOnPoly(P, args.x, args.y), cum = cumLengths(P), L = cum[cum.length - 1] ?? 0;
  const c0 = cum[q.i] ?? 0, c1 = cum[q.i + 1] ?? c0, sCut = c0 + q.t * (c1 - c0);
  const wAt = lerpAt(W, q.i, q.t), gap = wAt + CUT_EXTRA_GAP_M;
  const at = (s: number): Pt => pointAlong(P, cum, closed ? ((s % L) + L) % L : clamp(s, 0, L));
  const pos = r.nodes.map((_, j) => cum[Math.min(j * SEG, cum.length - 1)] ?? 0);
  const sA = sCut - gap / 2, sB = sCut + gap / 2;
  type Piece = { ids: NodeId[]; startNew: boolean; endNew: boolean };
  const pieces: Piece[] = [];
  const idx = r.nodes.map((_, j) => j);
  if (closed) {
    const order = idx.map(j => ({ j, rel: (((pos[j] ?? 0) - sB) % L + L) % L }))
      .filter(o => o.rel > 1 && o.rel < L - gap - 1).sort((a, b) => a.rel - b.rel);
    pieces.push({ ids: order.map(o => r.nodes[o.j] as NodeId), startNew: true, endNew: true });
  } else {
    const A = idx.filter(j => (pos[j] ?? 0) < sA - 1), B = idx.filter(j => (pos[j] ?? 0) > sB + 1);
    if (A.length) pieces.push({ ids: A.map(j => r.nodes[j] as NodeId), startNew: false, endNew: true });
    if (B.length) pieces.push({ ids: B.map(j => r.nodes[j] as NodeId), startNew: true, endNew: false });
  }
  const cA = at(sA), cB = at(sB);
  const shapeOf = (p: Piece): Pt[] => [
    ...(p.startNew ? [cB] : []),
    ...p.ids.map(id => { const nd = draft.nodes[id]; return [nd?.x ?? 0, nd?.y ?? 0] as Pt; }),
    ...(p.endNew ? [cA] : []),
  ];
  const kept = pieces.filter(p => { const s = shapeOf(p); return s.length >= 2 && polyLen(s) > 3; });

  const made: RoadId[] = [];
  const { kind } = r;
  kept.forEach((p, k) => {
    const ids = [
      ...(p.startNew ? [addNode(draft, cB[0], cB[1], wAt)] : []),
      ...p.ids,
      ...(p.endNew ? [addNode(draft, cA[0], cA[1], wAt)] : []),
    ];
    const id = k === 0 ? args.roadId : freshId(draft.roads);
    draft.roads[id] = { nodes: dedupeConsecutive(ids, false), closed: false, kind };
    made.push(id);
  });
  if (!made.length) delete draft.roads[args.roadId];
  pruneOrphans(draft);
  return ok(made);
}

/** Replaces dot `from` with dot `into` in every road and removes `from`. */
function mergeNodeInto(draft: PlanDoc, from: NodeId, into: NodeId): void {
  for (const r of Object.values(draft.roads)) if (r.nodes.includes(from)) r.nodes = dedupeConsecutive(r.nodes.map(n => (n === from ? into : n)), r.closed);
  delete draft.nodes[from];
}

/** How joinRoads joined. */
export type JoinKind = 'closed' | 'merged' | 'attached';

/**
 * Joins road `a` at one of its ends to road `b`, clicked at (x, y) (TD §5, as in the prototype):
 * - `b` is `a`: the road closes into a loop;
 * - near one of b's ends (open b): the two become one road (b's id disappears); dots under 1 m apart merge into one;
 * - anywhere else on b: a's end attaches to b at a shared dot (an existing one within 1.5 m, or a new one).
 */
export function joinRoads(draft: PlanDoc, args: { a: RoadId; endA: 'start' | 'end'; b: RoadId; x: number; y: number }): OpResult<JoinKind> {
  const ra = draft.roads[args.a], rb = draft.roads[args.b];
  if (!ra || !rb) return refuse('That road no longer exists.');
  if (ra.closed) return refuse(args.a === args.b ? 'That road is already a loop.' : 'A loop has no free end to join.');
  if (args.a === args.b) {
    const nodes = dedupeConsecutive(ra.nodes, true);
    if (new Set(nodes).size < 3) return refuse('A loop needs at least 3 dots.');
    ra.nodes = nodes; ra.closed = true;
    return ok('closed');
  }
  const aIds = args.endA === 'start' ? ra.nodes.slice().reverse() : ra.nodes.slice();   // a's joined end is last
  const aEnd = aIds[aIds.length - 1] as NodeId;

  if (!rb.closed) {
    const bPts = rb.nodes.map(id => draft.nodes[id]);
    const b0 = bPts[0], b1 = bPts[bPts.length - 1];
    const d0 = b0 ? Math.hypot(args.x - b0.x, args.y - b0.y) : 1e9, d1 = b1 ? Math.hypot(args.x - b1.x, args.y - b1.y) : 1e9;
    if (Math.min(d0, d1) < Math.max(30, 0.25 * polyLen(roadPoly(draft, rb).pts))) {
      const bIds = d0 <= d1 ? rb.nodes.slice() : rb.nodes.slice().reverse();               // b's joined end is first
      const bStart = bIds[0] as NodeId, ea = draft.nodes[aEnd], eb = draft.nodes[bStart];
      if (bStart === aEnd) bIds.shift();
      else if (ea && eb && Math.hypot(ea.x - eb.x, ea.y - eb.y) < 1
        && !Object.values(draft.roads).some(r => r.nodes.includes(bStart) && r.nodes.includes(aEnd))) {   // merging would fold another road
        mergeNodeInto(draft, bStart, aEnd); bIds.shift();
      }
      let nodes = dedupeConsecutive(aIds.concat(bIds), false), closed = false;
      if (nodes.length > 3 && nodes[0] === nodes[nodes.length - 1]) { nodes = nodes.slice(0, -1); closed = true; }
      const kind = rb.kind === 'main' ? 'main' : ra.kind;
      delete draft.roads[args.b];
      draft.roads[args.a] = { nodes, closed, kind };
      return ok('merged');
    }
  }
  if (rb.nodes.includes(aEnd)) return refuse('That end is already attached to the road.');
  const at = nodeOnRoad(draft, { roadId: args.b, x: args.x, y: args.y });
  if (!at.ok) return at;
  if (args.endA === 'start') ra.nodes.unshift(at.value); else ra.nodes.push(at.value);
  return ok('attached');
}
